'use client';

import { useId, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiArrowRightUpLine, RiImageLine } from '@remixicon/react';
import { useLocale, useTranslations } from 'next-intl';
import type { BlogPostSummary } from '@mentorivo/shared';

import { Link } from '@/i18n/navigation';
import { BLOG_FILTERS, type BlogFilter } from '@/lib/blog';
import { blogPostHref } from '@/lib/blog-api';
import { formatDate } from '@/lib/format';
import { dur, ease, spring, springFade, stagger } from '@/lib/motion';

/**
 * Yazı listesi — konu süzgeci ve ızgara.
 *
 * ── Veri nereden geliyor ──────────────────────────────────────────────────
 *
 * Prop olarak, sunucudan (`/[locale]/blog/page.tsx` → `blogApi.list`). Bu
 * bileşen bir zamanlan `lib/blog.ts`teki sabit listeyi ve `messages`
 * dosyasındaki başlıkları okuyordu; ikisi de kalktı (gerekçe `lib/blog.ts`).
 *
 * Süzgeç yine İSTEMCİDE çalışıyor: on iki kartın hepsi zaten geldi, konuya
 * basınca ağa çıkmanın kazancı yok ve her basışta yeni bir istek, kayan hapı
 * (`layoutId`) da beklemeye sokardı.
 *
 * ── Süzgeç neden sekme (tab) değil ────────────────────────────────────────
 *
 * Görüntü olarak sekmeye benziyor ama davranışı sekme değil: altındaki ızgara
 * bir "panel" değil, süzülmüş tek bir liste; klavyede ok tuşlarıyla dolaşmak
 * ve `tabpanel` bağlamak burada kullanıcıya yanlış bir zihin modeli verirdi.
 * Bu yüzden düğmeler `aria-pressed` taşıyor — "bu süzgeç basılı" demenin
 * doğru yolu — ve sonucun kaç yazı olduğu `aria-live` ile duyuruluyor;
 * ekran okuyucu kullanan biri de listenin değiştiğini duyuyor.
 *
 * ── Hareket: işaret kayar, liste yeniden oturur ───────────────────────────
 *
 * İki ayrı jest, ikisi de bir şey açıklıyor (DESIGN.md §4):
 *
 *   • Etkin süzgecin altındaki koyu hap `layoutId` ile TAŞINIYOR. Yeni bir hap
 *     belirip eskisi kaybolsaydı "iki ayrı şey" olurdu; kayan tek hap "aynı
 *     seçim şimdi burada" diyor.
 *   • Izgara `key={topic}` ile yeniden kuruluyor ve hücreler kısa bir dalgayla
 *     oturuyor. Bu, `lib/motion.ts`'teki tablo dalgasının gerekçesiyle aynı:
 *     dalga "yeni liste geldi" demenin kendisi.
 *
 * `AnimatePresence` + `layout` ile hücreleri yerinde kaydırmak denenmedi
 * bilinçli olarak: bu depoda `popLayout` özel bileşenlerde sessizce çalışmıyor
 * (uçan öğe akışta kalıp kabı iki katına çıkarıyor) ve dokuz kartın aynı anda
 * yer değiştirmesi DESIGN.md'nin istediği sakinliğin tersi. Yeniden kurulan
 * ızgara hem daha dürüst hem daha ucuz.
 *
 * Blur YOK — sahne girişlerinin imzası ama dokuz hücrede aynı anda ~15 öğelik
 * blur bütçesini (DESIGN.md §6) tek başına tüketirdi.
 */

const headV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const itemV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const gridV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const cellV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight } },
};

/* Yatay hat soldan çekiliyor, dikey ayraç yukarıdan iniyor — /about'taki
   hücre ızgaralarıyla aynı yön. İkisi de ayrı öğe, `border` değil: border
   animasyonlanamaz, transform animasyonlanır (DESIGN.md §1). */
const ruleV: Variants = {
  hidden: { opacity: 0, scaleX: 0 },
  show: { opacity: 1, scaleX: 1, transition: { duration: dur.base, ease: ease.out } },
};

const edgeV: Variants = {
  hidden: { opacity: 0, scaleY: 0 },
  show: { opacity: 1, scaleY: 1, transition: springFade(spring.default) },
};

const lineV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.snap) },
};

/* Kapak metinden ÖNCE geliyor ve ölçekten açılıyor — kart "önce bir görsel,
   sonra sözü" sırasıyla kuruluyor. Ölçek 0.98'den: daha derin bir başlangıç
   on iki kartın hepsinde birden fırlıyormuş gibi duruyor. */
const coverV: Variants = {
  hidden: { opacity: 0, scale: 0.98 },
  show: { opacity: 1, scale: 1, transition: springFade(spring.default) },
};

export function BlogPosts({ posts }: { posts: BlogPostSummary[] }) {
  const t = useTranslations('blog');
  const locale = useLocale();
  const uid = useId();

  const [filter, setFilter] = useState<BlogFilter>('all');

  const shown = useMemo(
    () => (filter === 'all' ? posts : posts.filter((p) => p.topic === filter)),
    [filter, posts],
  );

  return (
    <section className="posts" aria-labelledby={`${uid}-title`}>
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <div className="posts-frame">
            <motion.div className="posts-head" variants={headV} initial={false} animate="show">
              <motion.h2 className="posts-title" id={`${uid}-title`} variants={itemV}>
                {t('list.title')}
              </motion.h2>

              {/* Yatay kaydırma kabın KENDİSİNDE: dar ekranda altı süzgeç tek
                  satıra sığmıyor ve sayfa gövdesi asla kaymamalı
                  (DESIGN.md §2). */}
              <motion.div
                className="posts-filter"
                role="group"
                aria-label={t('list.filterLabel')}
                variants={itemV}
              >
                {BLOG_FILTERS.map((key) => {
                  const active = key === filter;

                  return (
                    <button
                      key={key}
                      type="button"
                      className="posts-chip"
                      aria-pressed={active}
                      onClick={() => setFilter(key)}
                    >
                      {active ? (
                        <motion.span
                          className="posts-chip-mark"
                          aria-hidden="true"
                          layoutId={`${uid}-mark`}
                          transition={spring.default}
                        />
                      ) : null}
                      <span className="posts-chip-text">
                        {key === 'all' ? t('list.all') : t(`topics.${key}`)}
                      </span>
                    </button>
                  );
                })}
              </motion.div>

              <motion.div className="posts-meta" variants={itemV}>
                <p className="posts-count" aria-live="polite">
                  {t('list.count', { count: shown.length })}
                </p>
              </motion.div>
            </motion.div>

            {shown.length === 0 ? (
              <p className="posts-empty">{t('list.empty')}</p>
            ) : (
              <motion.ul
                className="posts-grid"
                key={filter}
                variants={gridV}
                initial={false}
                animate="show"
              >
                {shown.map((post) => (
                  <motion.li className="posts-cell" key={post.slug} variants={cellV}>
                    <motion.span className="posts-rule" aria-hidden="true" variants={ruleV} />
                    {/* Ayraç her hücrede basılıp CSS ile seçiliyor: satır başı
                        hücrelerde gizli, orada çerçevenin kendi hattı var. */}
                    <motion.span className="posts-edge" aria-hidden="true" variants={edgeV} />

                    {/* Görsel alanı. Dosya yokken çizilen yer tutucu
                        anasayfadaki "Mentorivo ne yapar" bölümünün yer
                        tutucusuyla AYNI deri (`.shot-empty`): ızgara dokusu,
                        ortada bir görsel işareti, metin yok.

                        `alt=""` çünkü kapak kartın İÇİNDE ve yanında yazının
                        adı zaten metin olarak duruyor; okutulsaydı ekran
                        okuyucu aynı şeyi iki kez söylerdi. */}
                    <motion.div className="posts-cover" variants={coverV}>
                      {post.coverImage ? (
                        <img
                          className="posts-cover-img"
                          src={post.coverImage}
                          alt=""
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <span className="shot-empty" aria-hidden="true">
                          <RiImageLine size={26} aria-hidden="true" />
                        </span>
                      )}
                    </motion.div>

                    <motion.p className="posts-topic" variants={lineV}>
                      {t(`topics.${post.topic}`)}
                    </motion.p>

                    <motion.h3 className="posts-name" variants={lineV}>
                      {/* Bağlantı başlığın İÇİNDE ve kartın tamamına yayılıyor
                          (::after). Kartı `<a>` ile sarmak, içindeki tarih ve
                          konu etiketini de bağlantı adının parçası yapardı. */}
                      <Link className="posts-link" href={blogPostHref(post.slug)}>
                        {post.title}
                      </Link>
                    </motion.h3>

                    <motion.p className="posts-text" variants={lineV}>
                      {post.excerpt}
                    </motion.p>

                    <motion.p className="posts-foot" variants={lineV}>
                      <time dateTime={post.publishedAt}>
                        {formatDate(post.publishedAt, locale)}
                      </time>
                      <span aria-hidden="true">·</span>
                      <span>{t('list.read', { minutes: post.readMinutes })}</span>
                      <RiArrowRightUpLine className="posts-arrow" size={18} aria-hidden="true" />
                    </motion.p>
                  </motion.li>
                ))}
              </motion.ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
